import {
    Button as ChakraButton,
    HStack,
    Icon,
    Link,
    Tooltip,
    forwardRef,
} from '@chakra-ui/react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';

import { AnimatedTextWord } from './AnimatedTextWord';

import { checkHash } from '@/store/middlewares/modalChangesMiddleware';
import { _active, _focus, _hover } from './constants';
import { getZendeskSSOToken } from '@/API/AdminUser';
import {
    ZENDESK_HELP_CENTER_URL,
    ZENDESK_JWT_ENDPOINT,
    zenDeskLink,
} from 'hooks/useTrialStatus/constants';
import ScopeType from 'utils/enums/ScopeType';
import getRuntimeAppConfig from 'utils/runtimeAppConfig';

/** @type React.ForwardRefRenderFunction<HTMLButtonElement, import('@chakra-ui/react').ButtonProps> */
const Button = forwardRef((props, ref) => {
    return (
        <ChakraButton
            justifyContent="space-between"
            width="full"
            ref={ref}
            {...props}
        />
    );
});

const isCurrentWholesalerAllowedHelpCentre = (currentWholesaler) => {
    const appConfig = getRuntimeAppConfig();
    // helpCentreWholesalerIds is the pre-5.0.22 name for the same list.
    const allowedWholesalerIds =
        appConfig.wholesalerIdsEnablingPartnerHelpCentre ??
        appConfig.helpCentreWholesalerIds ??
        [];

    if (!Array.isArray(allowedWholesalerIds)) {
        return false;
    }

    return allowedWholesalerIds.includes(currentWholesaler);
};

const SidebarItem = ({ item, parentPath, isMenuCollapse, colorMode }) => {
    const { path, permissionName, scope } = item;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Clicking the menu item for the page you are already on is a deliberate
    // "reset this page" action, so it remounts via /reload/<path>. Invalidating
    // first means the remount also refetches: React Query would otherwise
    // rehydrate straight from cache inside its 10s staleTime, so the page came
    // back with the same data it already had.
    const resetCurrentPage = () => {
        // refetchType 'none' marks the queries stale without fetching here: the
        // page is about to unmount, and a request started now would still be in
        // flight when the new mount subscribes. React Query would dedupe against
        // it, so any page that writes its rows from inside its queryFn would run
        // that write against the old, unmounted instance and come back empty.
        // Stale-only means the remount issues the fetch itself.
        queryClient.invalidateQueries({ refetchType: 'none' });
        navigate(`/reload/${currentSubPath}`, {
            state: { pageReset: true },
        });
    };
    const dispatch = useDispatch();
    const { show, state, hash } = useSelector((state) => {
        return { ...state.modal };
    });
    const { currentWholesaler, currentPartner, currentCompany } = useSelector(
        (state) => {
            return { ...state.navigationLists };
        },
    );

    const { apiDocReader } = useSelector((state) => {
        return { ...state.login };
    });
    const currentPaths = window.location.pathname.split('/');
    const currentSubPath = currentPaths.length >= 2 && currentPaths[2];
    const isDirectingToSamePage = `/${currentSubPath}` === path;
    const { requiredScope: accountScope } = useSelector((state) => state.login);
    const supportLink =
        accountScope >= ScopeType.GLOBAL
            ? zenDeskLink
            : `${ZENDESK_JWT_ENDPOINT}?return_to=${encodeURIComponent(ZENDESK_HELP_CENTER_URL)}`;
    const canShowHelpCentre =
        accountScope >= ScopeType.WHOLESALER ||
        (accountScope === ScopeType.PARTNER &&
            isCurrentWholesalerAllowedHelpCentre(currentWholesaler));

    const onClick = async (e) => {
        e.preventDefault();

        if (item.permissionName === 'api') {
            return window.open('/docs/index.html', '_blank');
        }

        if (item.permissionName === 'helpCentre') {
            if (accountScope >= ScopeType.GLOBAL) {
                window.open(zenDeskLink, '_blank', 'noopener,noreferrer');
            } else {
                try {
                    const jwt = await getZendeskSSOToken();
                    const form = document.createElement('form');
                    form.method = 'POST';
                    form.action = `${ZENDESK_JWT_ENDPOINT}?return_to=${encodeURIComponent(ZENDESK_HELP_CENTER_URL)}`;
                    form.target = '_blank';
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = 'jwt';
                    input.value = jwt;
                    form.appendChild(input);
                    document.body.appendChild(form);
                    form.submit();
                    document.body.removeChild(form);
                } catch (e) {
                    console.error('Zendesk SSO failed', e);
                }
            }
            return;
        }

        if (state?.noNeedPrompt) {
            dispatch({
                type: 'REDIRECT',
                payload: {
                    pathname: '/' + parentPath + '/' + permissionName,
                    scope: scope ?? 20,
                },
            });

            if (isDirectingToSamePage) {
                resetCurrentPage();
                return;
            }

            navigate(`/${path}` || '');
            return;
        }

        // changes detected need to show window alert before navigation
        if (show && !checkHash({ originalHash: hash, currentObject: state })) {
            const value = window.confirm(
                'Are you sure you would like to switch to another menu? All unsaved changes will be lost.',
            );
            if (!value) {
                // do nothing
                return;
            }
        }

        dispatch({
            type: 'REDIRECT',
            payload: {
                pathname: '/' + parentPath + '/' + permissionName,
                scope: scope ?? 20,
            },
        });

        if (isDirectingToSamePage) {
            resetCurrentPage();
            return;
        }

        navigate(`${path}` || '');
    };

    if (item.permissionName === 'helpCentre') {
        if (!canShowHelpCentre) {
            return null;
        }

        return (
            <Link
                href={supportLink}
                target="_blank"
                _hover={{ color: 'inherit', textDecoration: 'none' }}
                _focus={{ boxShadow: 'none' }}
                onClick={(e) => e.preventDefault()}>
                <Tooltip
                    label={isMenuCollapse ? item.label : null}
                    hasArrow
                    placement="right-end">
                    <Button
                        bg="#424445"
                        _focus={{ bg: '#424445' }}
                        _hover={{ bg: '#2b2d2e' }}
                        _active={{ bg: '#2b2d2e' }}
                        size="sm"
                        onClick={onClick}
                        fontWeight={400}
                        paddingInline={isMenuCollapse ? '2' : null}
                        color="white">
                        <HStack spacing="3" aria-label="children route">
                            <Icon
                                fontSize="lg"
                                ml={isMenuCollapse ? '1.35rem' : 5}
                                mr={2}
                                as={item.Icon}
                            />
                            {!isMenuCollapse && (
                                <>
                                    <span
                                        style={{
                                            marginInline: '0 5px',
                                            fontSize: '14px',
                                        }}>
                                        {item.label}
                                    </span>
                                    <FaExternalLinkAlt
                                        fontSize="small"
                                        style={{
                                            marginLeft: 10,
                                            height: 10,
                                            width: 10,
                                        }}
                                        className="ml-0 inline-block"
                                    />
                                </>
                            )}
                        </HStack>
                    </Button>
                </Tooltip>
            </Link>
        );
    }

    if (item.label === 'API') {
        if (!apiDocReader) {
            return null;
        }

        return (
            <Link
                href="/docs/index.html"
                target="_blank"
                _hover={{ color: 'inherit', textDecoration: 'none' }}
                _focus={{ boxShadow: 'none' }}>
                <Tooltip
                    label={isMenuCollapse ? item.label : null}
                    hasArrow
                    placement="right-end">
                    <Button
                        bg="none"
                        _focus={{ bg: 'none' }}
                        _hover={colorMode[_hover]}
                        _active={{ bg: 'none' }}
                        size="sm"
                        onClick={onClick}
                        fontWeight={400}
                        paddingInline={isMenuCollapse ? '2' : null}
                        color="white">
                        <HStack spacing="3" aria-label="children route">
                            <Icon
                                fontSize="lg"
                                ml={isMenuCollapse ? '1.35rem' : 5}
                                mr={2}
                                as={item.Icon}
                            />
                            {isMenuCollapse ? null : (
                                <AnimatedTextWord text={item.label} />
                            )}
                            {!isMenuCollapse && (
                                <FaExternalLinkAlt
                                    fontSize="small"
                                    style={{
                                        marginLeft: 10,
                                        height: 10,
                                        width: 10,
                                    }}
                                    className="ml-0 inline-block"
                                />
                            )}
                        </HStack>
                    </Button>
                </Tooltip>
            </Link>
        );
    }

    return (
        <Link
            as={NavLink}
            to={{
                pathname: isDirectingToSamePage
                    ? `/reload/${currentSubPath}`
                    : item.path || '',
                search:
                    accountScope > 60
                        ? `?wholesalerID=${encodeURIComponent(currentWholesaler)}&partnerID=${encodeURIComponent(currentPartner)}&companyID=${encodeURIComponent(currentCompany)}`
                        : accountScope > 40
                          ? `?partnerID=${encodeURIComponent(currentPartner)}&companyID=${encodeURIComponent(currentCompany)}`
                          : accountScope > 20
                            ? `?companyID=${encodeURIComponent(currentCompany)}`
                            : '',
            }}
            _hover={{ color: 'inherit' }}
            style={{ marginTop: 0 }}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}>
            {({ isActive }) => {
                return (
                    <Tooltip
                        label={isMenuCollapse ? item.label : null}
                        hasArrow
                        placement="right-end">
                        <Button
                            _focus={
                                path.includes(`/${currentSubPath}`)
                                    ? colorMode[_focus]
                                    : {
                                          bg: 'none',
                                      }
                            }
                            _active={colorMode[_active]}
                            _hover={
                                isActive
                                    ? colorMode[_active]
                                    : colorMode[_hover]
                            }
                            fontWeight={isActive ? 'bold' : ''}
                            bg={
                                path.includes(`/${currentSubPath}`)
                                    ? colorMode[_active].bg
                                    : 'none'
                            }
                            size="sm"
                            onClick={onClick}
                            paddingInline={isMenuCollapse ? '2' : null}
                            color="white">
                            <HStack spacing="3" aria-label="children route">
                                <Icon
                                    fontSize="lg"
                                    ml={isMenuCollapse ? '1.35rem' : 5}
                                    mr={2}
                                    as={item.Icon}
                                />
                                {isMenuCollapse ? null : (
                                    <AnimatedTextWord text={item.label} />
                                )}
                            </HStack>
                        </Button>
                    </Tooltip>
                );
            }}
        </Link>
    );
};

export default SidebarItem;
